import { useEffect } from "react";

import { NavigationTabsContent, TabsContent } from "twyne-new.ui";

import { CodeEditor } from "@/components/CodeEditor";
import { useTailwindCssConfig } from "@/components/builder/utils/tailwindConfig/hooks";

import Preview from "./Preview";

const DEFAULT_TAILWIND_CSS_CONFIG = `@theme {
  /*
   * The @theme directive is used to define design tokens and theme values in Tailwind CSS v4.
   * This replaces the theme configuration object from tailwind.config.js in v3.
   * Here you define custom properties (CSS variables) that will be available throughout your design system.
   *
   * Example usage:
   * - Define custom colors, spacing, typography, and other design tokens
   * - Values defined here can be referenced in utility classes
   * - Supports CSS custom properties syntax with fallbacks
   */

  /* Example: Custom color definition */
  /* --color-primary: #3b82f6; */

  /* Example: Custom typography definition */
  /* --font-sans: ui-sans-serif, system-ui, sans-serif; */

  /* Example: Custom spacing definition */
  /* --spacing-custom: 1.5rem; */

  /* Example: Custom border radius definition */
  /* --radius-custom: 0.5rem; */

  /* Example: Custom shadow definition */
  /* --shadow-custom: 0 4px 6px -1px rgb(0 0 0 / 0.1); */
}

@layer base {
  /*
   * The @layer base directive contains base styles that should be applied globally.
   * This is similar to a CSS reset or normalize.css - it sets foundational styles
   * for HTML elements before any utility classes are applied.
   *
   * Example usage:
   * - Reset default browser styles
   * - Set base typography styles
   * - Define global element styles
   * - Apply consistent styling across all elements
   */

  /* Example: Basic reset for all elements */
  /*
  *,
  ::before,
  ::after {
    box-sizing: border-box;
    border-width: 0;
    border-style: solid;
    border-color: theme(colors.gray.200);
  }
  */

  /* Example: HTML base styles */
  /*
  html {
    line-height: 1.5;
    font-family: theme(fontFamily.sans);
  }
  */

  /* Example: Body base styles */
  /*
  body {
    margin: 0;
    color: theme(colors.gray.900);
    background-color: theme(colors.white);
  }
  */
}

@layer components {
  /*
   * The @layer components directive is for component-level styles.
   * These are reusable component patterns that you use frequently in your project.
   * Think of these as your custom component library - buttons, cards, forms, etc.
   *
   * Example usage:
   * - Create reusable component styles
   * - Define complex multi-class patterns
   * - Build your design system components
   * - Abstract common UI patterns
   */

  /* Example: Button component */
  /*
  .btn {
    @apply inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors;
    @apply focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2;
    @apply disabled:opacity-50 disabled:pointer-events-none;
    padding: theme(spacing.2) theme(spacing.4);
  }
  */

  /* Example: Card component */
  /*
  .card {
    @apply rounded-lg border bg-white shadow-sm p-6;
  }
  */

  /* Example: Form input component */
  /*
  .form-input {
    @apply w-full rounded-md border border-gray-300 px-3 py-2;
    @apply focus:outline-none focus:ring-2 focus:ring-blue-500;
  }
  */
}

@layer utilities {
  /*
   * The @layer utilities directive contains utility classes that you want to add
   * to Tailwind's existing utility layer. These are small, single-purpose classes
   * that can be combined to build more complex designs.
   *
   * Example usage:
   * - Create custom utility classes
   * - Add project-specific helper classes
   * - Extend Tailwind's built-in utilities
   * - Create responsive and state-variant utilities
   */

  /* Example: Custom layout utility */
  /*
  .center {
    @apply flex items-center justify-center;
  }
  */

  /* Example: Custom spacing utility */
  /*
  .p-safe {
    padding: max(1rem, env(safe-area-inset-top))
             max(1rem, env(safe-area-inset-right))
             max(1rem, env(safe-area-inset-bottom))
             max(1rem, env(safe-area-inset-left));
  }
  */

  /* Example: Custom animation utility */
  /*
  .animate-fade-in {
    animation: fade-in 0.3s ease-in-out;
  }
  */
}

/* Example: Animation keyframes (can be placed outside layers) */
/*
@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
*/
`

function CSSEditor({ value, onChange, className }) {
  const { value: cssConfig } = useTailwindCssConfig()
  const { tailwindCssConfig } = value || {}

  useEffect(() => {
    onChange('tailwindCssConfig', typeof cssConfig === 'string' && cssConfig ? cssConfig : DEFAULT_TAILWIND_CSS_CONFIG)
  }, [cssConfig, onChange])

  return <CodeEditor value={tailwindCssConfig} onChange={value => onChange('tailwindCssConfig', value)} width='100%' height='100%' language='css' className={className} />
}

export default function TailwindCssConfig({ isOpen, value, onChange }) {
  if (!isOpen) return null

  return <>
    <div className="basis-6/12">
      <CSSEditor value={value} onChange={onChange} />
    </div>
    <div className="basis-6/12">
      <Preview />
    </div>
  </ >
}
